import { tx } from "../lib/miniI18n";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Phone, Lock, ArrowLeft, ShieldCheck, Gift, Sparkles, Coins, User, CheckCircle2 } from "lucide-react";
import api, { setToken, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Checkbox } from "../components/ui/checkbox";
import { BrandLogo } from "../components/BrandLogo";
import { AdminRecoverForm } from "../components/AdminRecoverForm";

/* The OTP card stays on screen for the whole 5-minute validity (never a vanishing toast) */
const OtpWhere = ({ mobile, sent, testPrefix }) => {
  if (sent?.demo_otp) return (
    <div className="rounded-xl bg-amber-50 border-2 border-amber-300 p-3 text-center" data-testid={`${testPrefix}-otp-demo`}>
      <div className="text-[11px] font-bold text-amber-800">உங்க OTP (5 நிமிஷம் valid):</div>
      <div className="font-mono font-black text-3xl tracking-[0.4em] text-slate-900" data-testid={`${testPrefix}-otp-demo-code`}>{sent.demo_otp}</div>
      <div className="text-[10px] text-amber-700">இந்த number-ஐ கீழே type பண்ணுங்க — இது மறையாது</div>
    </div>
  );
  return <p className="text-xs text-slate-500 text-center" data-testid={`${testPrefix}-otp-where`}>{sent?.channel === "whatsapp" ? `WhatsApp (+91 ${mobile})-ல வந்த OTP-ஐ type பண்ணுங்க` : `Enter the OTP sent to +91 ${mobile}`}</p>;
};

export default function Auth() {
  const navigate = useNavigate();
  const { setUser, login, syncLang } = useAuth();
  const staffEntry = typeof window !== "undefined" && (window.location.pathname === "/staff" || new URLSearchParams(window.location.search).has("staff") || !!localStorage.getItem("rl_staff"));
  const [mode, setMode] = useState(staffEntry ? "login" : "customer"); // customer (mobile only) | signup (first time) | login (agent/admin password — only via /staff or on a staff device) | forgot | admin_recover
  const [loading, setLoading] = useState(false);

  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [otp, setOtp] = useState("");
  const [referral, setReferral] = useState("");
  const [age, setAge] = useState(false);
  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [otpSent, setOtpSent] = useState(null);

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const code = qs.get("code") || qs.get("ref");
    if (code) { setReferral(code.toUpperCase()); setMode("signup"); }
    const src = qs.get("src") || qs.get("utm_source");
    if (src) { localStorage.setItem("rl_src", src.toLowerCase().slice(0, 30)); setMode("signup"); }
    const rm = qs.get("rm");
    if (rm) { localStorage.setItem("rl_rm", rm.toLowerCase().slice(0, 12)); if (!localStorage.getItem("rl_src")) localStorage.setItem("rl_src", "remarketing"); setMode("signup"); }
    const via = qs.get("via");
    if (via) {
      localStorage.setItem("rl_via", via.toLowerCase().slice(0, 20));
      const shop = (qs.get("shop") || "").toUpperCase().slice(0, 12);
      if (shop) localStorage.setItem("rl_shop", shop); else localStorage.removeItem("rl_shop");
      let dev = localStorage.getItem("rl_device"); if (!dev) { dev = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("rl_device", dev); }
      if (code) api.post("/poster/scan", { code: code.toUpperCase(), via, shop: shop || null, device: dev }).catch(() => {});
      setMode("signup");
    }
    const locked = qs.get("locked");
    if (locked) toast.error(locked, { duration: 8000 });
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const switchMode = (m) => { setMode(m); setStep(1); setOtp(""); setPassword(""); setConfirm(""); };

  /* Customers: mobile number only. Unknown number → first-time flow (name + OTP). Staff number → password form. */
  const customerLogin = async () => {
    if (!/^[6-9]\d{9}$/.test(mobile)) { toast.error("10 இலக்க mobile number போடுங்க"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/customer-login", { mobile });
      setToken(data.token); localStorage.setItem("rl_has_logged_in", "1"); setUser(data.user);
      syncLang(data.user);
      if (data.user?.first_login) localStorage.setItem("rl_first_login_welcome", JSON.stringify(data.user.welcome || {}));
      toast.success(`வணக்கம் ${data.user?.name || ""} 🙏`);
      navigate("/");
    } catch (e) {
      const st = e.response?.status;
      if (st === 404) { setMode("signup"); setStep(1); toast.message("புது number — பேர் சொல்லி OTP வாங்குங்க", { duration: 6000 }); }
      else if (st === 403 && /Agent/.test(e.response?.data?.detail || "")) { setMode("login"); toast.message("Agent / Admin — password போடுங்க"); }
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const doLogin = async () => {
    setLoading(true);
    try {
      const user = await login(mobile, password);
      localStorage.setItem("rl_has_logged_in", "1");
      if (user.first_login && user.role === "customer") localStorage.setItem("rl_first_login_welcome", JSON.stringify(user.welcome || {}));
      if (user.role === "admin" || user.role === "agent") localStorage.setItem("rl_staff", "1");
      toast.success("Welcome back!");
      navigate(user.role === "admin" ? "/admin" : user.role === "agent" ? "/agent" : "/");
    } catch (e) {
      const st = e.response?.status;
      toast.error(formatApiError(e.response?.data?.detail));
      if (st === 401 && /^[6-9]\d{9}$/.test(mobile)) toast.message("Customer-ஆ? ← Customer தட்டி mobile number மட்டும் போடுங்கள். Agent/Admin password மறந்தா “Forgot Password?”", { duration: 8000 });
    } finally { setLoading(false); }
  };

  const sendOtp = async (purpose) => {
    if (!/^[6-9]\d{9}$/.test(mobile)) { toast.error("Enter a valid 10-digit mobile number"); return; }
    if (otpSent && otpSent.mobile === mobile && otpSent.purpose === purpose && resendIn > 0) { setStep(2); return; }  // code already on its way — just go back to the code screen
    setLoading(true);
    try {
      const { data } = await api.post("/auth/send-otp", { mobile, purpose, referral_code: purpose === "signup" ? (referral || null) : null });
      setStep(2);
      setOtpSent({ ...data, mobile, purpose });
      setResendIn(data.resend_after || 30);
      if (data.demo_otp) toast.success(`OTP: ${data.demo_otp}`, { duration: 90000 });
      else if (data.channel === "whatsapp") toast.success("OTP WhatsApp-ல வந்திருக்கும் — பாருங்க", { duration: 60000 });
      else toast.success("OTP sent to your mobile", { duration: 60000 });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const doSignup = async () => {
    if (otp.length < 4) return toast.error("OTP போடுங்க");
    if (firstName.trim().length < 2) return toast.error("பேர் சொல்லுங்க");
    if (!age) return toast.error("Please confirm you are 18+");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        mobile, otp, name: firstName.trim(), referral_code: referral || null, age_confirmed: age, src: localStorage.getItem("rl_src") || null, via: localStorage.getItem("rl_via") || null, shop: localStorage.getItem("rl_shop") || null, rm: localStorage.getItem("rl_rm") || null,
      });
      setToken(data.token); localStorage.setItem("rl_has_logged_in", "1");
      setUser(data.user);
      toast.success("Account created! Welcome to Royal Lakshmi 👑");
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const doReset = async () => {
    if (otp.length < 4) return toast.error("Enter the OTP");
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { mobile, otp, password, confirm_password: confirm });
      toast.success("Password reset! Please login.");
      switchMode("login");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const input = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-slate-900 placeholder-slate-400 outline-none focus:ring-2 focus:ring-blue-500/30";
  const btn = "w-full rl-gold-bg font-bold py-3.5 rounded-full flex items-center justify-center gap-2 active:scale-[0.99] transition-transform";

  return (
    <div className="rl-frame min-h-screen flex flex-col">
      {/* Casino promo hero */}
      <div className="relative px-6 pt-8 pb-12 text-white overflow-hidden" style={{ background: "linear-gradient(160deg,#059669 0%,#0f766e 45%,#0f172a 100%)" }}>
        <button onClick={() => navigate("/")} className="flex items-center gap-1 text-emerald-100 text-sm mb-6" data-testid="auth-back-btn">
          <ArrowLeft className="w-4 h-4" /> {tx("Back")}
        </button>
        <div className="flex items-center gap-2 mb-5">
          <BrandLogo className="w-11 h-11 rounded-xl ring-2 ring-white/30" testId="auth-logo-img" />
          <div>
            <div className="font-display font-black text-lg tracking-wide">{tx("ROYAL LAKSHMI")}</div>
            <div className="text-[10px] text-emerald-100 tracking-widest">{tx("PREMIUM LOTTERY & PLAY")}</div>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-3"><Coins className="w-6 h-6 text-yellow-300" /><div><div className="font-extrabold text-base leading-tight">{tx("20% Extra on First Recharge")}</div><div className="text-xs text-emerald-100">{tx("Bonus play money added instantly")}</div></div></div>
          <div className="flex items-center gap-3"><ShieldCheck className="w-6 h-6 text-yellow-300" /><div><div className="font-extrabold text-base leading-tight">{tx("Instant UPI Recharge")}</div><div className="text-xs text-emerald-100">{tx("Winnings paid fast to your bank / UPI")}</div></div></div>
          <div className="flex items-center gap-3"><Sparkles className="w-6 h-6 text-yellow-300" /><div><div className="font-extrabold text-base leading-tight">{tx("Free Lucky Spin")}</div><div className="text-xs text-emerald-100">{tx("Win ₹5 to ₹100 — every 6 hours")}</div></div></div>
        </div>
      </div>

      {/* Form sheet */}
      <div className="flex-1 bg-white rounded-t-3xl -mt-6 px-6 pt-8 pb-10 shadow-[0_-8px_30px_rgba(0,0,0,0.12)]">
        {mode === "login" || mode === "forgot" || mode === "admin_recover" ? (
          <div className="flex items-center justify-between mb-5">
            <div className="font-black text-slate-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-600" />Agent / Admin login</div>
            {!localStorage.getItem("rl_staff") && <button onClick={() => switchMode("customer")} className="text-xs text-blue-600 font-bold" data-testid="auth-tab-customer">← Customer</button>}
          </div>
        ) : (
          <div className="flex items-center justify-between mb-5">
            <div className="font-black text-slate-900 text-lg">{mode === "signup" ? "முதல் தடவை · First time" : "Login"}</div>
            {staffEntry && <button onClick={() => switchMode("login")} className="text-[11px] text-slate-500 font-bold" data-testid="auth-tab-login">Agent / Admin →</button>}
          </div>
        )}

        {mode === "customer" && (
          <div className="space-y-4 rl-fade-up" data-testid="customer-login">
            <div className="relative"><Phone className="absolute left-3.5 top-4 w-4 h-4 text-blue-500" />
              <input className={input + " pl-11 text-lg font-bold tracking-wider"} placeholder="Mobile Number" inputMode="numeric" maxLength={10} autoFocus
                value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && customerLogin()} data-testid="customer-mobile-input" />
            </div>
            <button onClick={customerLogin} disabled={loading} className={btn + " text-base py-4"} data-testid="customer-login-btn">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} உள்ள வா · Login
            </button>
            <p className="text-[11px] text-slate-500 text-center">Mobile number மட்டும் போதும் · Password இல்லை · புது number-ஆ இருந்தா பேர் + OTP ஒரே ஒரு தடவை</p>
          </div>
        )}

        {mode === "login" && (
          <div className="space-y-4 rl-fade-up">
            <div className="relative"><Phone className="absolute left-3.5 top-4 w-4 h-4 text-blue-500" />
              <input className={input + " pl-11"} placeholder={tx("Mobile Number")} inputMode="numeric" maxLength={10}
                value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))} data-testid="login-mobile-input" />
            </div>
            <div className="relative"><Lock className="absolute left-3.5 top-4 w-4 h-4 text-blue-500" />
              <input className={input + " pl-11"} type="password" placeholder={tx("Password")}
                value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
            </div>
            <div className="flex items-center justify-between">
              <button onClick={() => switchMode("admin_recover")} className="text-[11px] text-slate-400 font-semibold" data-testid="admin-recover-link">Admin recovery</button>
              <button onClick={() => switchMode("forgot")} className="text-xs text-blue-600 font-semibold" data-testid="forgot-link">{tx("Forgot Password?")}</button>
            </div>
            <button onClick={doLogin} disabled={loading} className={btn} data-testid="login-submit-btn">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign In
            </button>
          </div>
        )}

        {mode === "signup" && (
          <div className="space-y-4 rl-fade-up" data-testid="first-time-signup">
            <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 text-sm font-mono font-black text-slate-800 flex items-center justify-between" data-testid="signup-mobile-shown">+91 {mobile}<button onClick={() => switchMode("customer")} className="text-[11px] font-sans font-bold text-blue-600" data-testid="signup-change-mobile">மாத்து</button></div>
            {step === 1 && (
              <>
                <p className="text-sm text-slate-700 text-center font-semibold">புது number 🙏 OTP வாங்கி, பேர் சொன்னா போதும் — password இல்லை.</p>
                <label className="flex items-start gap-2 text-xs text-slate-600 px-1">
                  <Checkbox checked={age} onCheckedChange={setAge} data-testid="signup-age-checkbox" className="mt-0.5 border-blue-400 data-[state=checked]:bg-blue-600" />
                  {tx("I confirm I am 18 years or older and accept the Terms. Play responsibly.")}
                </label>
                <button onClick={() => { if (!age) return toast.error("18+ confirm பண்ணுங்க"); sendOtp("signup"); }} disabled={loading} className={btn} data-testid="signup-sendotp-btn">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} OTP வாங்கு · Get OTP
                </button>
              </>
            )}
            {step === 2 && (
              <>
                <OtpWhere mobile={mobile} sent={otpSent} testPrefix="signup" />
                <input className={input + " text-center tracking-[0.5em] font-mono text-2xl"} placeholder="____" inputMode="numeric" maxLength={6} autoFocus
                  value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && otp.length >= 4 && setStep(3)} data-testid="signup-otp-input" />
                <button disabled={resendIn > 0} onClick={() => sendOtp("signup")} className="text-xs text-blue-600 block mx-auto font-semibold disabled:text-slate-400" data-testid="resend-otp-btn">
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend OTP"}
                </button>
                <button onClick={() => { if (otp.length < 4) return toast.error("OTP போடுங்க"); setStep(3); }} className={btn} data-testid="signup-otp-next-btn">OTP சரி → பேர்</button>
              </>
            )}
            {step === 3 && (
              <>
                <div className="relative"><User className="absolute left-3.5 top-4 w-4 h-4 text-blue-500" />
                  <input className={input + " pl-11"} placeholder="உங்க பேர் · Your Name" maxLength={40} autoComplete="given-name" autoFocus
                    value={firstName} onChange={(e) => setFirstName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSignup()} data-testid="signup-name-input" />
                </div>
                <div className="relative"><Gift className="absolute left-3.5 top-4 w-4 h-4 text-blue-500" />
                  <input className={input + " pl-11"} placeholder={tx("Agent / Invite Code (optional)")}
                    value={referral} onChange={(e) => setReferral(e.target.value.toUpperCase())} data-testid="signup-referral-input" />
                </div>
                <button onClick={doSignup} disabled={loading} className={btn} data-testid="signup-submit-btn">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}<CheckCircle2 className="w-4 h-4" /> உள்ள வா · Login
                </button>
                <p className="text-[11px] text-slate-500 text-center">அடுத்த தடவை mobile number மட்டும் போட்டா போதும்</p>
                <button onClick={() => setStep(2)} className="text-xs text-slate-500 block mx-auto" data-testid="signup-back-otp-btn">← OTP மாத்தணுமா?</button>
              </>
            )}
          </div>
        )}

        {mode === "admin_recover" && (
          <div className="space-y-4 rl-fade-up">
            <div className="flex items-center gap-2 text-slate-900 font-semibold"><ShieldCheck className="w-5 h-5 text-amber-500" />Admin password recovery</div>
            <AdminRecoverForm onDone={(m) => { switchMode("login"); setMobile(m); }} />
            <button onClick={() => switchMode("login")} className="text-xs text-blue-600 font-semibold block mx-auto" data-testid="recover-back">← Back to sign in</button>
          </div>
        )}

        {mode === "forgot" && (
          <div className="space-y-4 rl-fade-up">
            <div className="flex items-center gap-2 text-slate-900 font-semibold"><ShieldCheck className="w-5 h-5 text-blue-500" />{tx("Reset Password")}</div>
            {step === 1 ? (
              <>
                <div className="relative"><Phone className="absolute left-3.5 top-4 w-4 h-4 text-blue-500" />
                  <input className={input + " pl-11"} placeholder={tx("Registered Mobile Number")} inputMode="numeric" maxLength={10}
                    value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))} data-testid="forgot-mobile-input" />
                </div>
                <button onClick={() => sendOtp("reset")} disabled={loading} className={btn} data-testid="forgot-sendotp-btn">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} Send OTP
                </button>
              </>
            ) : (
              <>
                <OtpWhere mobile={mobile} sent={otpSent} testPrefix="forgot" />
                <input className={input + " text-center tracking-[0.5em] font-mono text-2xl"} placeholder="____" inputMode="numeric" maxLength={6}
                  value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} data-testid="forgot-otp-input" />
                <input className={input} type="password" placeholder={tx("New Password")} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="forgot-password-input" />
                <input className={input} type="password" placeholder={tx("Confirm New Password")} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="forgot-confirm-input" />
                <button onClick={doReset} disabled={loading} className={btn} data-testid="forgot-submit-btn">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} Reset Password
                </button>
              </>
            )}
            <button onClick={() => switchMode("login")} className="text-xs text-slate-500 block mx-auto" data-testid="forgot-back-btn">{tx("Back to Sign In")}</button>
          </div>
        )}

        <p className="text-[10px] text-slate-400 text-center mt-8">18+ only · Play responsibly</p>
      </div>
    </div>
  );
}
